/**
 * A USD wallet paying a CAD session, converted (Warren, 2026-10-25T02:52:41Z,
 * option 1: "Convert at the moment they pay. The wallet stays in USD. When it
 * pays a CAD session, the system converts at that day's Bank of Canada rate
 * and shows the rate on the receipt.").
 *
 * The rules, one place:
 *   - A CAD wallet with enough balance pays a CAD session as before. Only
 *     when it cannot may the USD wallet pay, converted.
 *   - The rate is the Bank of Canada FXUSDCAD rate (CAD per USD, the latest
 *     published business day), read by the Api and PINNED on the session at
 *     start, like its prices and fees. Settlement uses only the pin: it never
 *     waits on a rate lookup, and the driver pays the rate the screen showed.
 *     No rate means no conversion: the USD wallet is not offered at a CAD
 *     charger (FX_RATE_UNAVAILABLE), never a guessed rate.
 *   - Every debit rounds in the driver's favour: the USD taken for a CAD
 *     amount is that amount divided by the rate, rounded DOWN to the cent.
 *   - The value of a USD balance at a CAD charger is the most CAD it can pay
 *     under that rule (walletFxValueCents). The minimum check, the screen
 *     ("US$29.12, about CA$40.76 at today's rate") and the settlement
 *     funding all use that one number.
 *
 * Integer arithmetic on the rate in ten-thousandths (FXUSDCAD is published
 * to four decimals), so each rounding is exact. Pure: no I/O.
 */
export const WALLET_FX_PAIR = 'USDCAD';
export const WALLET_FX_SOURCE = 'bank_of_canada';
const DAY = /^\d{4}-\d{2}-\d{2}$/;
/** The rate in ten-thousandths (1.3993 is 13993), or null outside the sane
 *  band the processor quote also enforces (0.5 to 3 CAD per USD). */
export function walletFxRateUnits(rate) {
    if (typeof rate !== 'number' || !Number.isFinite(rate) || rate < 0.5 || rate > 3)
        return null;
    return Math.round(rate * 10_000);
}
/** A usable rate to pin: pair, source, date and band all as expected. */
export function isWalletFxRate(value) {
    const r = value;
    return !!r && r.pair === WALLET_FX_PAIR && r.source === WALLET_FX_SOURCE
        && typeof r.rateDate === 'string' && DAY.test(r.rateDate) && walletFxRateUnits(r.rate) !== null;
}
/** The session's pin when it applies to a price in `sessionCurrency`, else
 *  null (no pin, a malformed one, or a session that no longer bills CAD).
 *  Null means the wallet in the session's own currency pays, as before. */
export function walletFxPinFor(session, sessionCurrency) {
    const pin = session?.wallet_fx_at_start;
    if (!pin || pin.pair !== WALLET_FX_PAIR || pin.source !== WALLET_FX_SOURCE || pin.wallet_currency !== 'USD'
        || pin.session_currency !== 'CAD' || typeof pin.rate_date !== 'string' || !DAY.test(pin.rate_date)
        || walletFxRateUnits(pin.rate) === null)
        return null;
    if (String(sessionCurrency ?? '').trim().toUpperCase() !== 'CAD')
        return null;
    return pin;
}
/**
 * The most of a CAD price a USD balance pays at this rate, in CAD cents: the
 * largest amount whose debit (rounded down) still fits the balance. Without
 * a conversion (null) the balance is already in the price's currency.
 */
export function walletFxValueCents(walletCents, fx) {
    const balance = Math.max(0, Math.trunc(Number.isFinite(walletCents) ? walletCents : 0));
    if (!fx)
        return balance;
    const units = walletFxRateUnits(fx.rate);
    if (units === null || balance === 0)
        return 0;
    return Math.floor(((balance + 1) * units - 1) / 10_000);
}
/** The USD to take for a CAD amount: the amount divided by the rate, rounded
 *  DOWN to the cent (the driver's favour). Identity without a conversion. */
export function walletFxDebitCents(sessionCents, fx) {
    const amount = Math.max(0, Math.trunc(Number.isFinite(sessionCents) ? sessionCents : 0));
    if (!fx)
        return amount;
    const units = walletFxRateUnits(fx.rate);
    if (units === null)
        throw new RangeError('A converted wallet debit needs a valid pinned rate');
    return Math.floor((amount * 10_000) / units);
}
/** The least USD to add so the balance's CAD value reaches `requiredCents`. */
export function walletFxTopUpCents(walletCents, requiredCents, fx) {
    const units = walletFxRateUnits(fx.rate);
    const required = Math.max(0, Math.trunc(requiredCents));
    if (units === null || required === 0)
        return 0;
    const balance = Math.max(0, Math.trunc(walletCents));
    const least = Math.max(1, Math.ceil((required * 10_000 + 1) / units) - 1);
    return Math.max(0, least - balance);
}
/** The rate as printed on the screen and the receipt: four decimals. */
export function formatWalletFxRate(rate) {
    const units = walletFxRateUnits(rate);
    return units === null ? String(rate) : (units / 10_000).toFixed(4);
}
/** The pin the driver-start path writes from the rate the gate used. */
export function walletFxPinFromRate(rate, now = new Date()) {
    if (!isWalletFxRate(rate))
        throw new RangeError('A wallet conversion pin needs a valid rate');
    return {
        pair: WALLET_FX_PAIR,
        rate: walletFxRateUnits(rate.rate) / 10_000,
        rate_date: rate.rateDate,
        source: WALLET_FX_SOURCE,
        wallet_currency: 'USD',
        session_currency: 'CAD',
        recorded_at: rate.recordedAt ?? null,
        pinned_at: now,
    };
}
/** The debit record written beside Session.wallet_debit_cents. */
export function walletFxDebitRecord(pin, paidCents) {
    return {
        pair: pin.pair,
        rate: pin.rate,
        rate_date: pin.rate_date,
        source: pin.source,
        wallet_currency: pin.wallet_currency,
        session_currency: pin.session_currency,
        debit_cents: walletFxDebitCents(paidCents, pin),
        paid_cents: Math.max(0, Math.trunc(paidCents)),
    };
}
//# sourceMappingURL=walletFx.js.map